import React, { useState, useEffect } from 'react';
import { ApplicationLog } from '../../types/ApplicationLog';
import { logApi } from '../../services/logApi';
import { AlertCircle, Info, AlertTriangle, XCircle, RefreshCw, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

export const LogViewer: React.FC = () => {
  const [logs, setLogs] = useState<ApplicationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const [logsData, count] = await Promise.all([
        logApi.getLogs(pageSize, pageNumber, levelFilter || undefined),
        logApi.getLogCount(levelFilter || undefined)
      ]);
      setLogs(logsData);
      setTotalCount(count);
    } catch (err) {
      setError('Kunne ikke hente logger');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [pageNumber, levelFilter]);

  const handleClearLogs = async () => {
    if (!window.confirm('Er du sikker på at du vil slette alle logger?')) return;
    
    try {
      await logApi.clearLogs();
      await fetchLogs();
    } catch (err) {
      setError('Kunne ikke slette logger');
    }
  };

  const toggleExpanded = (id: number) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedLogs(newExpanded);
  };

  const getLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'information':
        return <Info className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getLevelBgColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'information':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="p-4">
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold">Application Logs</h2>
        <div className="flex gap-2">
          <select
            value={levelFilter}
            onChange={(e) => {
              setLevelFilter(e.target.value);
              setPageNumber(1);
            }}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Alle nivåer</option>
            <option value="Error">Error</option>
            <option value="Warning">Warning</option>
            <option value="Information">Information</option>
          </select>
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Oppdater
          </button>
          <button
            onClick={handleClearLogs}
            className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" />
            Slett
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-2 p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                  Nivå
                </th>
                <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                  Tidspunkt
                </th>
                <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                  Melding
                </th>
                <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                  Maskin
                </th>
                <th className="px-1 py-1"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-xs">
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className={`hover:bg-gray-50 ${getLevelBgColor(log.level)}`}>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {getLevelIcon(log.level)}
                        <span className="font-medium">{log.level.substring(0, 4)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap text-gray-600 font-mono">
                      {(() => {
                        const date = new Date(log.timeStamp);
                        const year = date.getFullYear();
                        const day = date.getDate().toString().padStart(2, '0');
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const hours = date.getHours().toString().padStart(2, '0');
                        const minutes = date.getMinutes().toString().padStart(2, '0');
                        const seconds = date.getSeconds().toString().padStart(2, '0');
                        const ms = date.getMilliseconds().toString().padStart(3, '0');
                        return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}.${ms}`;
                      })()}
                    </td>
                    <td className="px-3 py-1 text-gray-900 max-w-md truncate" title={log.message}>
                      {log.message}
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap text-gray-600">
                      {log.machineName?.split('-')[0] || '-'}
                    </td>
                    <td className="px-1 py-1">
                      {log.exception && (
                        <button
                          onClick={() => toggleExpanded(log.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {expandedLogs.has(log.id) ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedLogs.has(log.id) && log.exception && (
                    <tr>
                      <td colSpan={5} className="px-3 py-2 bg-gray-50">
                        <div className="text-xs">
                          <div className="font-medium mb-1">Exception:</div>
                          <pre className="bg-gray-900 text-gray-100 p-2 rounded text-xs overflow-x-auto">
                            {log.exception}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && !loading && (
          <div className="text-center py-4 text-gray-500 text-sm">
            Ingen logger funnet
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-3 py-2 border-t border-gray-200 flex items-center justify-between">
            <div className="text-xs text-gray-600">
              {(pageNumber - 1) * pageSize + 1}-{Math.min(pageNumber * pageSize, totalCount)} av {totalCount}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                disabled={pageNumber === 1}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ◀
              </button>
              <span className="px-2 py-1 text-xs">
                {pageNumber}/{totalPages}
              </span>
              <button
                onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                disabled={pageNumber === totalPages}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ▶
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};